import React, { useEffect, useMemo, useState } from "react";
import {
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CContainer,
  CFormInput,
  CFormLabel,
  CRow,
} from "@coreui/react";
import { apiClient } from "services/Api";
import LoadingScreen from "components/globals/LoadingScreen";
import ErrorPage from "components/globals/ErrorPage";
import SearchSelect from "components/forms/SearchSelect";
import DataTableComponent from "components/DataTableComponent";
import { cilCalendar } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import {
  formatDateRange,
  groupConsecutiveAbsences,
} from "./_utils/absenceGrouping";

const MONTH_OPTIONS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

const getInitials = (firstName, lastName) => {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (!first && !last) return "?";
  const firstChar = first ? first[0] : "";
  const lastChar = last ? last[0] : "";
  return `${firstChar}${lastChar}`.toUpperCase();
};

const Absences = () => {
  const now = new Date();
  const currentMonth = now.getMonth() + 1;

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(currentMonth);
  const [listedUsers, setListedUsers] = useState([]);
  const [draftYear, setDraftYear] = useState(now.getFullYear());
  const [draftMonth, setDraftMonth] = useState(currentMonth);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null);
    setError(false);

    apiClient
      .get(`internal-management/absences/?year=${year}&month=${month}`)
      .then((response) => {
        setData(response.data || {});
      })
      .catch(() => {
        setError(true);
      });
  }, [year, month]);

  const userOptions = useMemo(() => {
    const usersMap = data?.users || {};
    return Object.entries(usersMap)
      .map(([id, name]) => ({
        value: Number(id),
        label: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt"));
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const validIds = new Set(userOptions.map((option) => option.value));
    setListedUsers((current) =>
      current.filter((userId) => validIds.has(userId))
    );
  }, [data, userOptions]);

  const filteredAbsences = useMemo(() => {
    const absences = data?.absences || [];
    return listedUsers.length
      ? absences.filter((absence) =>
          listedUsers.includes(Number(absence.teamwork_id))
        )
      : absences;
  }, [data, listedUsers]);

  const rows = useMemo(
    () => groupConsecutiveAbsences(filteredAbsences),
    [filteredAbsences]
  );

  const columns = useMemo(
    () => [
      {
        name: "Data",
        selector: (row) => row.start || "",
        cell: (row) => formatDateRange(row.start, row.end),
        sortable: true,
      },
      {
        name: "Colaborador",
        selector: (row) =>
          `${row.first_name || ""} ${row.last_name || ""}`.trim(),
        sortable: true,
        cell: (row) => {
          const fullName = `${row.first_name || ""} ${
            row.last_name || ""
          }`.trim();
          const initials = getInitials(row.first_name, row.last_name);
          return (
            <div className="holidays-user">
              {row.avatar_url ? (
                <img
                  src={row.avatar_url}
                  alt={fullName}
                  className="holidays-user-avatar"
                />
              ) : (
                <span className="holidays-user-avatar holidays-user-avatar--fallback">
                  {initials}
                </span>
              )}
              <div className="holidays-user-info">
                <div className="holidays-user-name">
                  {fullName || `#${row.teamwork_id}`}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        name: "Motivo",
        selector: (row) => row.motive || "N/A",
        sortable: true,
        className: "text-center",
      },
      {
        name: "Horário",
        selector: (row) => row.schedule || "-",
        sortable: true,
        className: "text-center",
      },
      {
        name: "Comprovativo",
        selector: (row) => (row.proof ? "Comprovado" : "Sem prova"),
        cell: (row) => (
          <CBadge color={row.proof ? "success" : "secondary"}>
            {row.proof ? "Comprovado" : "Sem prova"}
          </CBadge>
        ),
        sortable: true,
        className: "text-center",
      },
    ],
    []
  );

  const handleApplyFilters = () => {
    setYear(draftYear);
    setMonth(draftMonth);
  };

  if (error) {
    return <ErrorPage message="Erro ao carregar ausências." />;
  }

  if (!data) {
    return <LoadingScreen message="A carregar ausências..." />;
  }

  return (
    <CContainer>
      <div className="management-filters mb-4">
        <div className="management-filters-header">
          <div className="management-section-title">Filtros</div>
          <CButton color="primary" size="sm" onClick={handleApplyFilters}>
            Aplicar filtros
          </CButton>
        </div>
        <CRow className="g-3 management-filters-grid">
          <CCol xs={12} md={4} lg={2}>
            <CFormLabel>Ano</CFormLabel>
            <CFormInput
              type="number"
              min={2000}
              max={now.getFullYear() + 1}
              value={draftYear}
              onChange={(event) => {
                const nextYear = Number(event.target.value);
                if (Number.isFinite(nextYear) && nextYear > 0) {
                  setDraftYear(nextYear);
                }
              }}
            />
          </CCol>
          <CCol xs={12} md={4} lg={3}>
            <CFormLabel>Mês</CFormLabel>
            <SearchSelect
              options={MONTH_OPTIONS}
              value={draftMonth}
              onChange={(value) => {
                // Limpar o mês (value === null) define draftMonth a 0, que o
                // backend interpreta como "ano completo".
                if (Number.isFinite(Number(value))) {
                  setDraftMonth(Number(value));
                }
              }}
              placeholder="Todos os meses"
            />
          </CCol>
          <CCol xs={12} md={8} lg={4}>
            <CFormLabel>Colaboradores</CFormLabel>
            <SearchSelect
              options={userOptions}
              isMulti
              value={listedUsers}
              onChange={(values) => setListedUsers(values)}
              placeholder="Todos os colaboradores"
            />
          </CCol>
        </CRow>
      </div>

      <CCard className="mb-4">
        <CCardHeader>
          Ausências -{" "}
          {month
            ? `${MONTH_OPTIONS.find((item) => item.value === month)?.label} ${year}`
            : `Todos os meses ${year}`}
        </CCardHeader>
        <CCardBody>
          <DataTableComponent columns={columns} data={rows} search={false} />
        </CCardBody>
      </CCard>

      <CRow className="gy-3 mt-4 holidays-guidelines">
        <CCol lg={12}>
          <CCard className="holidays-info-card holidays-info-card--primary h-100">
            <CCardBody>
              <div className="d-flex align-items-center gap-3 mb-2">
                <div className="holidays-info-icon">
                  <CIcon icon={cilCalendar} />
                </div>
                <div className="holidays-info-title">Procedimento Interno</div>
              </div>
              <p>
                O colaborador deve marcar antecipadamente a indisponibilidade e
                categorizar conforme os tipos disponíveis, através da plataforma
                Teamwork.
              </p>
              <p>
                A designação do evento deverá ser:
                <br />
                <strong>INDISPONIBILIDADE | Nome </strong>
              </p>
              <p className="mb-0">
                Na descrição do evento deverá indicar se a indisponibilidade
                será justificada, da seguinte forma:
                <br />
                <strong>Justificação: Sim </strong>
              </p>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default Absences;
